import type { GatsbyBrowser } from 'gatsby';

/* Custom Accordion */
const PAGE_MARKER = '#page-header-dishwasher-experience';
const CONTAINER = '.l1-content-page';
const HEADING = 'h3.cds-typography--variant-headline';
const BODY = 'div.cds-typography--variant-body';
const TOGGLE_ATTR = 'data-accordion-toggle';
const DURATION = 300;
const EASING = 'ease';

const ZERO = {
  height: '0px',
};

const snapshot = (el: HTMLElement) => ({
  height: getComputedStyle(el).height,
});

const initAccordion = () => {
  if (!document.querySelector(PAGE_MARKER)) return;

  const container = document.querySelector(CONTAINER);
  if (!container) return;

  container.querySelectorAll<HTMLElement>(HEADING).forEach((heading) => {
    const body = heading.nextElementSibling as HTMLElement | null;
    if (!body || !body.matches(BODY)) return;
    if (heading.hasAttribute(TOGGLE_ATTR)) return;

    heading.setAttribute(TOGGLE_ATTR, '');
    heading.setAttribute('role', 'button');
    heading.setAttribute('tabindex', '0');
    heading.setAttribute('aria-expanded', 'false');

    body.setAttribute('inert', '');
  });
};

const toggle = (heading: HTMLElement) => {
  const body = heading.nextElementSibling as HTMLElement | null;
  if (!body) return;

  const willOpen = heading.getAttribute('aria-expanded') !== 'true';

  const applyState = () => {
    heading.setAttribute('aria-expanded', String(willOpen));
    body.toggleAttribute('inert', !willOpen);
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    applyState();

    return;
  }

  const from = snapshot(body);
  body.getAnimations().forEach((a) => a.cancel());

  applyState();
  const to = willOpen ? snapshot(body) : ZERO;

  body.animate([from, to], { duration: DURATION, easing: EASING });
};

const getHeading = (target: EventTarget | null) =>
  (target as HTMLElement | null)?.closest?.<HTMLElement>(`[${TOGGLE_ATTR}]`) ?? null;

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  document.addEventListener('click', (e) => {
    const heading = getHeading(e.target);
    if (heading) toggle(heading);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const heading = getHeading(e.target);
    if (!heading) return;
    e.preventDefault();
    toggle(heading);
  });
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = () => {
  requestAnimationFrame(() => setTimeout(initAccordion, 0));
};
